import { useEffect, useRef } from 'react'

type Node = { x: number; y: number; vx: number; vy: number }

/**
 * Lightweight neural-network / plexus animation rendered on a canvas.
 * Nodes drift slowly, connect to nearby nodes and the cursor with faint
 * lines. Honours prefers-reduced-motion (renders a single static frame)
 * and pauses while the tab is hidden.
 */
export function NeuralBackground({ className = '' }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const el = canvasRef.current
    if (!el) return
    const context = el.getContext('2d')
    if (!context) return
    const canvas: HTMLCanvasElement = el
    const g: CanvasRenderingContext2D = context

    // Draw a single static frame (no rAF loop) when the user opts out of motion
    // OR on small/touch screens: the O(n²) plexus loop is pure main-thread cost
    // and hurts TBT/battery on phones for a purely decorative background.
    const reduceMotion =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      window.matchMedia('(max-width: 767px), (pointer: coarse)').matches
    const LINK_DIST = 140
    const rand = (a: number, b: number) => a + Math.random() * (b - a)

    let width = 0
    let height = 0
    let nodes: Node[] = []
    let raf = 0
    let idleHandle = 0
    const mouse = { x: -9999, y: -9999, active: false }

    function build() {
      const rect = canvas.getBoundingClientRect()
      width = rect.width
      height = rect.height
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      g.setTransform(dpr, 0, 0, dpr, 0, 0)
      const count = Math.min(70, Math.max(20, Math.floor((width * height) / 16000)))
      nodes = Array.from({ length: count }, () => ({
        x: rand(0, width),
        y: rand(0, height),
        vx: rand(-0.28, 0.28),
        vy: rand(-0.28, 0.28),
      }))
    }

    function update() {
      for (const n of nodes) {
        n.x += n.vx
        n.y += n.vy
        if (n.x <= 0 || n.x >= width) n.vx *= -1
        if (n.y <= 0 || n.y >= height) n.vy *= -1
      }
    }

    function draw() {
      g.clearRect(0, 0, width, height)

      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i]
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j]
          const d = Math.hypot(a.x - b.x, a.y - b.y)
          if (d < LINK_DIST) {
            g.strokeStyle = `rgba(56, 189, 248, ${(1 - d / LINK_DIST) * 0.33})`
            g.lineWidth = 1
            g.beginPath()
            g.moveTo(a.x, a.y)
            g.lineTo(b.x, b.y)
            g.stroke()
          }
        }
        if (mouse.active) {
          const md = Math.hypot(a.x - mouse.x, a.y - mouse.y)
          const reach = LINK_DIST * 1.5
          if (md < reach) {
            g.strokeStyle = `rgba(125, 211, 252, ${(1 - md / reach) * 0.5})`
            g.lineWidth = 1
            g.beginPath()
            g.moveTo(a.x, a.y)
            g.lineTo(mouse.x, mouse.y)
            g.stroke()
          }
        }
      }

      for (const n of nodes) {
        g.beginPath()
        g.arc(n.x, n.y, 1.6, 0, Math.PI * 2)
        g.fillStyle = 'rgba(148, 197, 253, 0.9)'
        g.fill()
      }
    }

    function loop() {
      update()
      draw()
      raf = requestAnimationFrame(loop)
    }

    function start() {
      if (raf) return
      raf = requestAnimationFrame(loop)
    }
    function stop() {
      if (raf) cancelAnimationFrame(raf)
      raf = 0
    }

    const onPointerMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      mouse.active = x >= 0 && y >= 0 && x <= rect.width && y <= rect.height
      mouse.x = x
      mouse.y = y
    }
    const onPointerLeave = () => {
      mouse.active = false
    }
    const onVisibility = () => {
      if (document.hidden) stop()
      else if (!reduceMotion) start()
    }

    const ro = new ResizeObserver(() => {
      build()
      if (reduceMotion) draw()
    })
    ro.observe(canvas)

    build()
    if (reduceMotion) {
      draw()
    } else {
      window.addEventListener('pointermove', onPointerMove, { passive: true })
      window.addEventListener('pointerleave', onPointerLeave)
      document.addEventListener('visibilitychange', onVisibility)
      // Defer the first frame until the browser is idle so canvas work doesn't
      // compete with hydration / LCP during the initial load.
      const idle =
        window.requestIdleCallback || ((cb: () => void) => window.setTimeout(cb, 200))
      idleHandle = idle(() => {
        if (!document.hidden) start()
      }) as unknown as number
    }

    return () => {
      if (idleHandle) {
        if (window.cancelIdleCallback) window.cancelIdleCallback(idleHandle)
        else clearTimeout(idleHandle)
      }
      stop()
      ro.disconnect()
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerleave', onPointerLeave)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
    />
  )
}
